/**
 * SEO block: keyword-dense, visually understated section that expands
 * on the topical authority of the site for both Google + LLM search.
 * Uses semantic H2/H3s and internal links to reinforce keyword clusters.
 */
export default function SeoBlock() {
  return (
    <section
      id="global-forwarders"
      data-testid="seo-block"
      aria-label="India customs clearance partner for global freight forwarders"
      className="border-b border-[#E4E7EC] bg-[#F9FAFB]"
    >
      <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-24">
        <p className="overline mb-4">§ 12 — Reference</p>
        <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-[#101828] sm:text-4xl md:text-5xl">
          The India Desk for Global Freight Forwarders,
          <br className="hidden md:block" />
          Couriers, and Relocation Agents.
        </h2>
        <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-3">
          <article>
            <h3 className="font-display text-xl font-bold tracking-tight text-[#101828]">
              Licensed Customs House Agent (CHA) in India
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#475467]">
              A Customs House Agent — also called Customs Broker or CHA — is
              licensed under the Customs Brokers Licensing Regulations 2018
              (CBLR 2018) to file Bills of Entry, classify goods under HSN, and
              settle duty on behalf of importers. Custom Clearance Partner is a
              licensed CHA and ICEGATE eSANCHIT-enabled desk headquartered in
              Delhi NCR, working exclusively as the India representative for
              global freight forwarders and logistics companies.
            </p>
          </article>
          <article>
            <h3 className="font-display text-xl font-bold tracking-tight text-[#101828]">
              Coverage across every major Indian gateway
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#475467]">
              We operate air-express customs clearance at Delhi Indira Gandhi
              International (IGI / DEL), Mumbai Chhatrapati Shivaji Maharaj
              International (CSMIA / BOM), Bengaluru Kempegowda International
              (KIA / BLR), Chennai (MAA), Hyderabad Rajiv Gandhi International
              (RGIA / HYD), Kolkata Netaji Subhas Chandra Bose International
              (NSCBI / CCU), and Cochin (COK). We also handle Inland Container
              Depot clearance at ICD Tughlakabad and ICD Patparganj.
            </p>
          </article>
          <article>
            <h3 className="font-display text-xl font-bold tracking-tight text-[#101828]">
              Serving forwarders worldwide
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#475467]">
              We work with freight forwarders, courier companies, and shipping
              lines from{" "}
              <strong>Germany, Japan, Vietnam, Thailand, Russia, Iraq,
              Belgium, Qatar, Australia</strong>, and every other origin that
              ships into India — including United States, United Kingdom,
              United Arab Emirates, Saudi Arabia, Singapore, Hong Kong, China,
              South Korea, Netherlands, Canada, Türkiye, France, and Italy. If
              you're a logistics company looking for a reliable India desk,
              WhatsApp us at{" "}
              <a href="https://wa.me/917800320078" className="font-semibold text-[#D92D20] underline">
                +91 78003 20078
              </a>
              .
            </p>
          </article>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2">
          <article>
            <h3 className="font-display text-xl font-bold tracking-tight text-[#101828]">
              Search terms our clients typically use to find us
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#475467]">
              customs clearance India, best customs clearance agent Delhi,
              customs house agent India (CHA), customs broker India, India
              customs partner for freight forwarders, air express customs
              clearance in India, IGI Delhi customs clearance agent, Mumbai
              customs clearance broker, Bangalore CHA for electronics import,
              Hyderabad pharma customs clearance, Chennai customs house agent,
              Kolkata customs clearance, Cochin customs clearance for Gulf
              forwarders, ICD Tughlakabad customs broker, courier bill of entry
              filing India, unaccompanied baggage clearance India, Transfer of
              Residence (TR) customs India, e-commerce customs clearance India,
              last mile delivery India for forwarders, ATA Carnet clearance
              India, DGR customs clearance India, cold-chain pharma clearance
              India, cross-border D2C India, hire customs broker India 24/7.
            </p>
          </article>
          <article>
            <h3 className="font-display text-xl font-bold tracking-tight text-[#101828]">
              Why global logistics companies pick a specialist CHA over a courier
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#475467]">
              Global couriers (DHL, FedEx, UPS, TNT) run their own India
              networks — but they operate as generalists on standard duty
              structures. A specialist licensed CHA like Custom Clearance
              Partner handles HSN classification with precision, negotiates
              with the appraiser at first exam, files eSANCHIT documents in
              advance, and settles duty on our own credit. The result:{" "}
              <strong>sub-6-hour clearance turnaround</strong>,{" "}
              <strong>99.2% first-time exam pass rate</strong>, and{" "}
              <strong>direct WhatsApp visibility per HAWB</strong>. For
              forwarders selling India lanes to end-customers, this is the
              difference between winning and losing the account.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
}
